/**
 * Assets slice - manages video/image/document assets
 */

/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { cleanupBlobUrl, recreateBlobUrlIfNeeded } from '../../lib/assetProcessing';
import { clearAssetFiles } from '../../lib/assetFileStorage';

const initialState = {
  videos: [],
  images: [],
  documents: [],
  questions: [],
  texts: [],
  audios: [],
  assignedSections: [],
  placeholders: [],
  isLoading: false,
  error: null,
};

const assetsSlice = createSlice({
  name: 'assets',
  initialState,
  reducers: {
    addAsset: (state, action) => {
      // Recreate blob URL if needed (e.g., when restoring via undo/redo)
      const asset = recreateBlobUrlIfNeeded(action.payload);
      
      if (asset.type === 'video') {
        // Add video assets regardless of source_type (upload or project)
        state.videos.unshift(asset);
      } else if (asset.type === 'image') {
        state.images.unshift(asset);
      } else if (asset.type === 'document') {
        state.documents.unshift(asset);
      } else if (asset.type === 'question') {
        state.questions.unshift(asset);
      } else if (asset.type === 'text') {
        state.texts.unshift(asset);
      } else if (asset.type === 'audio') {
        state.audios.unshift(asset);
      } else if (asset.type === 'assigned-section') {
        state.assignedSections.unshift(asset);
      } else if (asset.type === 'placeholder') {
        state.placeholders.unshift(asset);
      }
    },
    addAssets: (state, action) => {
      const reversed = [...action.payload].reverse();
      reversed.forEach((assetPayload) => {
        // Recreate blob URL if needed (e.g., when restoring via undo/redo)
        const asset = recreateBlobUrlIfNeeded(assetPayload);
        
        if (asset.type === 'video') {
          // Add video assets regardless of source_type (upload or project)
          state.videos.unshift(asset);
        } else if (asset.type === 'image') {
          state.images.unshift(asset);
        } else if (asset.type === 'document') {
          state.documents.unshift(asset);
        } else if (asset.type === 'question') {
          state.questions.unshift(asset);
        } else if (asset.type === 'text') {
          state.texts.unshift(asset);
        } else if (asset.type === 'audio') {
          state.audios.unshift(asset);
        } else if (asset.type === 'assigned-section') {
          state.assignedSections.unshift(asset);
        } else if (asset.type === 'placeholder') {
          state.placeholders.unshift(asset);
        }
      });
    },
    removeAsset: (state, action) => {
      const assetId = action.payload;

      const allAssets = [
        ...state.videos,
        ...state.images,
        ...state.documents,
        ...state.audios,
        ...state.assignedSections,
      ];
      const asset = allAssets.find((a) => a.id === assetId);

      if (asset) {
        cleanupBlobUrl(asset.url);
        if (asset.thumbnail_url) {
          cleanupBlobUrl(asset.thumbnail_url);
        }
        // Note: We don't remove the File object from external storage here
        // because it might be needed for undo/redo. File objects are only
        // cleared when clearAssets is called.
      }

      state.videos = state.videos.filter((a) => a.id !== assetId);
      state.images = state.images.filter((a) => a.id !== assetId);
      state.documents = state.documents.filter((a) => a.id !== assetId);
      state.questions = state.questions.filter((a) => a.id !== assetId);
      state.texts = state.texts.filter((a) => a.id !== assetId);
      state.audios = state.audios.filter((a) => a.id !== assetId);
      state.assignedSections = state.assignedSections.filter((a) => a.id !== assetId);
      state.placeholders = state.placeholders.filter((a) => a.id !== assetId);
    },
    updateAsset: (state, action) => {
      const { id, changes } = action.payload;
      const updateInBucket = (arr) => {
        const idx = arr.findIndex((a) => a.id === id);
        if (idx !== -1) {
          arr[idx] = { ...arr[idx], ...changes };
        }
      };
      updateInBucket(state.videos);
      updateInBucket(state.images);
      updateInBucket(state.documents);
      updateInBucket(state.questions);
      updateInBucket(state.texts);
      updateInBucket(state.audios);
      updateInBucket(state.assignedSections);
      updateInBucket(state.placeholders);
      if (state.projectVideos) updateInBucket(state.projectVideos);
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearAssets: (state) => {
      // Cleanup blob URLs before clearing
      [...state.videos, ...state.images, ...state.documents, ...state.audios].forEach((asset) => {
        if (asset.url && asset.url.startsWith('blob:')) {
          cleanupBlobUrl(asset.url);
        }
        if (asset.thumbnail_url && asset.thumbnail_url.startsWith('blob:')) {
          cleanupBlobUrl(asset.thumbnail_url);
        }
      });
      // Clear all File objects from external storage
      clearAssetFiles();
      
      state.videos = [];
      state.images = [];
      state.documents = [];
      state.questions = [];
      state.texts = [];
      state.audios = [];
      state.assignedSections = [];
      state.placeholders = [];
    },
  },
});

export const { addAsset, addAssets, removeAsset, updateAsset, setLoading, setError, clearAssets } =
  assetsSlice.actions;

export const selectAllAssets = (state) => [
  ...state.assets.videos,
  ...state.assets.images,
  ...state.assets.documents,
  ...state.assets.questions,
  ...state.assets.texts,
  ...state.assets.audios,
  ...state.assets.assignedSections,
  ...state.assets.placeholders,
  ...(state.assets.projectVideos || []),
];

export const selectAssetById = (state, assetId) => {
  const allAssets = selectAllAssets(state);
  return allAssets.find((a) => a.id === assetId);
};

export default assetsSlice.reducer;

